import React from "react";
import { useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import DashboardPanelHeader from "components/DashboardPanelHeader";
import HighchartsPictorial from "highcharts/modules/pictorial";
import LeaseListPopUp from "./leaseListPopUp";
import DealsListPopUp from "./dealListPopup";

if (typeof HighchartsPictorial === "function") {
    HighchartsPictorial(Highcharts);
}

const LeadFunnelChart = ({ data, isLoading, isError }) => {
    const [status, setStatus] = useState("")
    const [isPopup, setIsPopup] = useState(false)
    const [isDealPopup, setIsDealPopup] = useState(false)
    const [header, setHeader] = useState("");

    const total = data?.totalLeadsGenerated ?? 0;
    const active = data?.activeLeads ?? 0;
    const deals = data?.dealsWon ?? 0;

    const safeData = [
        { name: "Total Leads", value: total, color: "#B0FDFE" },
        { name: "Active Leads", value: active, color: "#E3FED4" },
        { name: "Deals Won", value: deals, color: "#FAE146" },
    ];


    const handleClick = (label) => {
        const filters = {};

        if (label === "Deals Won") {
            setIsDealPopup(true);
            setHeader(label);
            return;
        }

        if (label === "Active Leads") {
            filters.CurrentStage = "Active";
        } else if (label === "Total Leads") {
            filters.CurrentStage = "";
        }

        setStatus(filters);
        setHeader(label);
        setIsPopup(true);
    };

    
    const handleDealClose = () => {
        setIsDealPopup(false);
    };


    const handleClose = () => {
        setIsPopup(false);
    };


    const options = {
        chart: {
            type: "pictorial",
            height: 420,
            backgroundColor: "transparent",
            ignoreHiddenSeries: true,
            animation: false
        },
        title: { text: "" },
        xAxis: { visible: false },
        yAxis: { visible: false },
        legend: { enabled: false },
        tooltip: {
            useHTML: true,
            followPointer: false,
            shared: false,
            enabled: true,
            outside: true,
            formatter: function () {
                return `
                <div style="font-family:Outfit;font-size:12px">
                    <b>${this.series.name}</b><br/>
                    Count: ${this.point.realValue}
                </div>
                `;
            },
        },
        plotOptions: {
            series: {
                stacking: "percent",
                cursor: "pointer",
                borderWidth: 0,
                pointPadding: 0,
                groupPadding: 0,
                stickyTracking: false,
                trackByArea: false,
                findNearestPointBy: "xy",
                point: {
                    events: {
                        click: function () {
                            handleClick(this.series.name, this.series.options.realValue);
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    useHTML: true,
                    formatter: function () {
                        return `
                        <div style="
                            text-align:center;
                            font-family:Outfit;
                            font-weight:600;
                            font-size:12px;
                        ">
                            ${this.series.name}: ${this.point.realValue}
                        </div>
                        `;
                    },
                },
                paths: [{
                    definition: "M480.15 0.510986V0.531986C316.002 0.531986 197.223 56.655 119.105 139.78C40.987 222.905 3.50699 332.801 0.884992 440.062C-1.74001 547.459 36.194 644.769 79.287 725.354C122.38 805.938 170.742 870.203 188.861 909.922C205.994 947.479 203.626 990.232 206.788 1033.17C209.95 1076.11 219.126 1119.48 260.261 1156.26C260.888 1156.83 261.679 1157.18 262.52 1157.27L274.021 1200.24C275.812 1214.45 275.053 1222.2 273.364 1229.45C261.44 1238.59 250.866 1253.57 283.323 1261.97V1283.88C249.425 1299.28 261.103 1315.14 283.323 1327.03L281.331 1342.96C249.673 1354.72 261.6 1377.5 282.645 1388.76V1403.36C256.094 1414.86 256.771 1436.12 283.323 1451.16V1473.73L349.035 1535.46L396.163 1582.58H565.433L619.193 1535.46C660.881 1505.66 698.191 1468.41 702.729 1451.49L687.226 1426.38C714.969 1420.61 718.256 1388.06 687.226 1382.78V1366.87C725.039 1359.03 715.965 1331.13 690.532 1325.04V1311.77C735.92 1292.94 715.774 1272.19 695.193 1267.29V1245.38C721.584 1240.94 721.209 1210.5 702.688 1201.19L711.682 1162.54C754.295 1134.83 762.81 1094.37 765.299 1051.47C767.789 1008.58 764.577 962.629 775.69 923.173C833.216 822.264 875.654 750.885 918.093 679.505C963.133 472.719 929.374 236.776 848.507 148.143C767.638 59.511 644.344 0.516987 480.15 0.516987Z"
                }],
            }
        },
        series: safeData.map(item => ({
            name: item.name,
            data: [{
                y: item.value === 0 ? 1 : item.value,
                realValue: item.value
            }],
            color: item.color,
        })),

        credits: { enabled: false },
    };

    return (
        <>
            <Box
                sx={{
                    background: "#fff",
                    border: "1px solid #E4E4E4",
                    borderRadius: 1,
                    width: "100%",
                }}
            >
                <DashboardPanelHeader eyebrow="Conversion flow" title="Leasing Funnel" subtitle="Track lead progression from initial interest through successful deal closure." />
                <Box sx={{ borderBottom: "1px solid #E4E4E4", p: 1.5, display: "none" }}>
                    <Typography sx={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "20px" }}>
                        Lead Funnel Visualization
                    </Typography>

                    <Typography sx={{ fontFamily: "Outfit", fontWeight: 300, fontSize: "14px", color: "#7D7F81" }}>
                        Conversion flow from total leads to successful deals
                    </Typography>
                </Box>

                {
                    isLoading && (
                        <Box
                            sx={{
                                minHeight: "100px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress size={32} />
                        </Box>
                    )
                }
                {
                    isError && (
                        <Box
                            sx={{
                                minHeight: "100px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    color: "#FF4F8B",
                                    textAlign: "center",
                                }}
                            >
                                Failed to load data. Please try again.
                            </Typography>
                        </Box>
                    )
                }

                {
                    !isError && !isLoading && <Box sx={{ p: 2 }}>
                        <HighchartsReact highcharts={Highcharts} options={options} />
                    </Box>
                }
            </Box >
            {
                isPopup && (
                    <LeaseListPopUp
                        header={header}
                        open={isPopup}
                        handleClose={handleClose}
                        filter={status}
                    />
                )
            }

            {
                isDealPopup && (
                    <DealsListPopUp
                        header={header}
                        open={isDealPopup}
                        handleClose={handleDealClose}
                    />
                )
            }
        </>
    );
};

export default LeadFunnelChart;
